import React, { Component, type ErrorInfo, type ReactNode } from 'react';

const arraysShallowEqual = (a: unknown[] | undefined, b: unknown[] | undefined): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return !a && !b;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  resetKeys?: unknown[];
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    console.error('ErrorBoundary caught error:', error);
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary details:', {
      error: error.message,
      componentStack: errorInfo.componentStack,
      componentName: this.props.componentName
    });
  }

  public componentDidUpdate(prevProps: Props): void {
    if (
      this.state.hasError &&
      !arraysShallowEqual(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.setState({ hasError: false, error: undefined });
    }
  }

  public render(): React.ReactNode {
    if (this.state.hasError) {
      // Return fallback or default error UI
      return this.props.fallback ?? (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          border: '1px solid #fecaca', 
          borderRadius: '8px',
          backgroundColor: '#fef2f2',
          margin: '20px',
          color: '#991b1b'
        }}>
          <h3>⚠️ Component Error</h3>
          <p>Something went wrong with {this.props.componentName ?? 'this component'}.</p>
          <details style={{ marginTop: '10px', textAlign: 'left' }}>
            <summary>Error Details</summary>
            <pre style={{ fontSize: '12px', marginTop: '10px' }}>
              {this.state.error?.message}
            </pre>
          </details>
          <button 
            onClick={() => window.location.reload()} 
            style={{ 
              marginTop: '10px', 
              padding: '8px 16px', 
              backgroundColor: '#dc2626', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px' 
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
